<template>
    <div class="integration-node-config">
        <div v-if="inputs && inputs.length > 0" class="inputs-section">
            <h6 class="section-title">{{ $t('inputs') }}</h6>
            
            <div v-for="input in inputs" :key="input.name" class="input-field-wrapper mb-3">
                <b-form-group
                    :label="$translateLabel(input.label, input.name)"
                    :label-for="`input-${input.name}`"
                    label-size="sm"
                    :description="$translateLabel(input.description, input.name)"
                >
                    <div class="input-with-toggle">
                        <!-- Expression mode toggle -->
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="expression-toggle mb-2"
                            @click="toggleExpressionMode(input.name)"
                        >
                            <b-icon :icon="isExpressionMode(input.name) ? 'code-square' : 'pencil'" />
                            {{ isExpressionMode(input.name) ? $t('expression') : $t('value') }}
                        </b-button>

                        <!-- Expression mode: code editor -->
                        <CodeEditor
                            v-if="isExpressionMode(input.name)"
                            :id="`input-${input.name}`"
                            v-model="content.inputs[input.name]"
                            :withButton="false"
                            :options="{
                                mode: 'javascript',
                                showFullscreenButton: false,
                                title: $translateLabel(input.label, input.name)
                            }"
                            :height="150"
                        />

                        <!-- Value mode: different inputs based on type -->
                        <template v-else>
                            <!-- String input -->
                            <b-form-input
                                v-if="input.type === 'string'"
                                :id="`input-${input.name}`"
                                v-model="content.inputs[input.name]"
                                :placeholder="input.default"
                            />

                            <!-- String array: tag input -->
                            <b-form-tags
                                v-else-if="input.type === 'string[]'"
                                :id="`input-${input.name}`"
                                :value="getArrayValue(input.name)"
                                @input="setInputValue(input.name, $event)"
                                separator=","
                                :placeholder="$translateLabel(input.description, input.name) || 'Add item and press Enter'"
                                remove-on-delete
                                variant="dark"
                                add-on-change
                            />

                            <!-- Number input -->
                            <b-form-input
                                v-else-if="input.type === 'number'"
                                :id="`input-${input.name}`"
                                v-model.number="content.inputs[input.name]"
                                type="number"
                                :placeholder="input.default"
                            />

                            <!-- Boolean toggle -->
                            <c-switch
                                v-else-if="input.type === 'boolean'"
                                :id="`input-${input.name}`"
                                v-model="content.inputs[input.name]"
                                color="success"
                                variant="pill"
                                :label="true"
                                dataOn="Yes"
                                dataOff="No"
                            />

                            <!-- Text area -->
                            <b-form-textarea
                                v-else-if="input.type === 'text'"
                                :id="`input-${input.name}`"
                                v-model="content.inputs[input.name]"
                                rows="3"
                                :placeholder="input.default"
                            />

                            <!-- Select dropdown -->
                            <v-select
                                v-else-if="input.type === 'select' && input.options"
                                :id="`input-${input.name}`"
                                v-model="content.inputs[input.name]"
                                :options="formatOptions(input.options)"
                                label="label"
                                :reduce="option => option.value"
                                :placeholder="input.default"
                            />

                            <!-- Object/Object[]: JSON code editor -->
                            <CodeEditor
                                v-else-if="input.type === 'object' || input.type === 'array' || input.type === 'object[]'"
                                :id="`input-${input.name}`"
                                :value="getJsonValue(input.name, input.type)"
                                @input="setInputValue(input.name, $event)"
                                :withButton="false"
                                :options="{
                                    mode: 'json',
                                    showFullscreenButton: true,
                                    title: $translateLabel(input.label, input.name)
                                }"
                                :height="200"
                            />

                            <!-- HTML: code editor with HTML mode -->
                            <CodeEditor
                                v-else-if="input.type === 'html'"
                                :id="`input-${input.name}`"
                                v-model="content.inputs[input.name]"
                                :withButton="false"
                                :options="{
                                    mode: 'html',
                                    showFullscreenButton: true,
                                    title: $translateLabel(input.label, input.name)
                                }"
                                :height="250"
                            />

                            <!-- Default: string input -->
                            <b-form-input
                                v-else
                                :id="`input-${input.name}`"
                                v-model="content.inputs[input.name]"
                                :placeholder="input.default"
                            />
                        </template>

                        <!-- Required indicator -->
                        <small v-if="input.required" class="text-danger">*</small>
                    </div>
                </b-form-group>
            </div>
        </div>

        <div v-if="outputs && outputs.length > 0" class="outputs-section mt-4">
            <h6 class="section-title">{{ $t('outputs') }}</h6>
            <div class="outputs-list">
                <div v-for="output in outputs" :key="output.name" class="output-item">
                    <span class="output-name">{{ output.name }}</span>
                    <span class="output-type">{{ output.type }}</span>
                    <small v-if="output.description" class="output-desc">{{ $translateLabel(output.description, output.name) }}</small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CodeEditor from '@/components/form/Editor/Code'
import VSelect from 'vue-select'

export default {
    components: {
        CodeEditor,
        VSelect
    },
    props: {
        value: {
            type: Object,
            required: true
        }
    },
    data() {
        return {};
    },
    computed: {
        content: {
            get() {
                return this.value || {};
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        expressionModes: {
            get() {
                return this.content.expressionModes || {};
            },
            set(val) {
                this.$emit('input', { ...this.content, expressionModes: val });
            }
        },
        nodeData() {
            return this.content || {};
        },
        inputs() {
            // Get inputs from the node metadata
            if (this.nodeData._integration && this.nodeData._integration.inputs) {
                return this.nodeData._integration.inputs;
            }
            return [];
        },
        outputs() {
            // Get outputs from the node metadata
            if (this.nodeData._integration && this.nodeData._integration.outputs) {
                return this.nodeData._integration.outputs;
            }
            return [];
        }
    },
    mounted() {
        // Initialize inputs and expressionModes objects if they don't exist
        const updates = {};
        if (!this.value.inputs) {
            updates.inputs = {};
        }
        if (!this.value.expressionModes) {
            updates.expressionModes = {};
        }
        if (Object.keys(updates).length > 0) {
            this.$emit('input', { ...this.value, ...updates });
        }
    },
    methods: {
        toggleExpressionMode(inputName) {
            const newModes = { ...this.expressionModes, [inputName]: !this.expressionModes[inputName] };
            this.$emit('input', { ...this.content, expressionModes: newModes });
        },
        isExpressionMode(inputName) {
            return this.expressionModes[inputName] || false;
        },
        formatOptions(options) {
            // If options is already array of strings, return as is
            if (Array.isArray(options) && typeof options[0] === 'string') {
                return options;
            }
            // If options is array of objects with label/value, format for v-select
            if (Array.isArray(options) && typeof options[0] === 'object') {
                return options.map(opt => ({
                    label: this.$translateLabel(opt.label, opt.value),
                    value: opt.value
                }));
            }
            return options;
        },
        getArrayValue(inputName) {
            var val = this.content.inputs && this.content.inputs[inputName];
            return Array.isArray(val) ? val : [];
        },
        getJsonValue(inputName, type) {
            var val = this.content.inputs && this.content.inputs[inputName];
            if (typeof val === 'string') return val;
            if (val !== undefined && val !== null) return JSON.stringify(val, null, 2);
            return type === 'object' ? '{}' : '[]';
        },
        setInputValue(inputName, value) {
            var inputs = { ...(this.content.inputs || {}), [inputName]: value };
            this.$emit('input', { ...this.content, inputs: inputs });
        }
    }
};
</script>

<style scoped>
.integration-node-config {
    padding: 0.5rem 0;
}

.section-title {
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.input-with-toggle {
    position: relative;
}

.expression-toggle {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
}

.expression-input,
.json-input {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    font-size: 0.85rem;
}

.outputs-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.output-item {
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 0.25rem;
    border-left: 3px solid #10b981;
}

.output-name {
    font-weight: 600;
    font-size: 0.85rem;
}

.output-type {
    font-size: 0.75rem;
    color: #6c757d;
    font-family: monospace;
}

.output-desc {
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 0.25rem;
}
</style>

<style>
.integration-node-config .b-form-tags {
    background-color: var(--bg-card) !important;
    color: var(--text-primary) !important;
    border-color: var(--border-default) !important;
}

.integration-node-config .b-form-tags.focus {
    background-color: var(--bg-card) !important;
    color: var(--text-primary) !important;
    border-color: var(--border-hover) !important;
    box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.1) !important;
}

.integration-node-config .b-form-tags .b-form-tags-input {
    background-color: transparent !important;
    color: var(--text-primary) !important;
}

.integration-node-config .b-form-tags .b-form-tag {
    background-color: var(--bg-card-secondary) !important;
    color: var(--text-primary) !important;
    border: 1px solid var(--border-default) !important;
}
</style>
